import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Section } from '../templates/Section';
import { Grid } from '../atoms/Grid';
import { Card } from '../atoms/Card';
import { Stack } from '../atoms/Stack';
import { Button } from '../atoms/Button';
import { Text } from '../atoms/Typography';
import { EmptyState } from '../atoms/EmptyState';
import { Icon } from '../atoms/Icon';
import { useTranslation } from '../../i18n/useTranslation';
import {
  useCameraSourceImages,
  useCameraSourcesQuery,
  useCreateCameraSourceMutation,
  useDeleteCameraSourceMutation,
  useUpdateCameraSourceMutation,
  useRevokeCameraSourceMutation,
} from '../../hooks/useCameraSources';
import type {
  CameraSource,
  CameraSourceImage,
  CameraSourceWithToken,
} from '../../api/cameraSources';
import { CameraSourceCard } from './CameraSourceCard';
import { ConfirmDialog } from '../ui/ConfirmDialog/ConfirmDialog';
import {
  CameraSourceForm,
  type CameraSourceFormValues,
} from './CameraSourceForm';
import { TokenRevealDialog } from './TokenRevealDialog';
import { RevokeConfirmationDialog } from './RevokeConfirmationDialog';
import { useToast } from '../../stores/toastStore';
import { buildApiUrl } from '../../lib/apiClient';
import './CameraSourcesPanel.css';
import { LoadErrorState } from '../feedback/LoadErrorState/LoadErrorState';

/** Header the camera/bridge must send the device token in (see backend deviceTokenAuth). */
const DEVICE_TOKEN_HEADER = 'X-Device-Token';
const LOADING_PLACEHOLDER_COUNT = 2;
/** Class on the section heading; focus returns here after a source is deleted. */
const SECTION_HEADING_CLASS = 'camera-sources-panel__section-title';

/** Absolute ingestion URL a device posts snapshots to. */
function ingestEndpointUrl(sourceId: string): string {
  const path = buildApiUrl(`/api/v1/camera-sources/${encodeURIComponent(sourceId)}/images`);
  try {
    return new URL(path, window.location.origin).toString();
  } catch {
    return path;
  }
}

interface LastReceivedImage {
  url: string;
  capturedAt: string;
}

/** Card thumbnail for a source's last received image (smallest available variant). */
function toLastReceivedImage(image: CameraSourceImage | undefined): LastReceivedImage | undefined {
  if (!image) return undefined;
  const url = image.thumbnailUrl ?? image.displayUrl ?? image.originalUrl;
  if (!url) return undefined;
  return { url, capturedAt: image.capturedAt ?? image.uploadedAt };
}

type FormState = { mode: 'create' } | { mode: 'edit'; source: CameraSource };

export interface CameraSourcesPanelProps {
  aquariumId: string | undefined;
}

/**
 * Integrations → "Cameras" (design spec #1436, formerly Settings → Camera
 * sources): the aquarium's camera sources with the create/edit form, the
 * one-time token reveal and the revoke / delete confirmations.
 */
export function CameraSourcesPanel({ aquariumId }: CameraSourcesPanelProps) {
  const { t } = useTranslation();
  const { showToast } = useToast();

  const { data: cameraSources, isError: sourcesError, refetch } = useCameraSourcesQuery(aquariumId);

  const createMutation = useCreateCameraSourceMutation(aquariumId);
  const updateMutation = useUpdateCameraSourceMutation(aquariumId);
  const revokeMutation = useRevokeCameraSourceMutation(aquariumId);
  const deleteMutation = useDeleteCameraSourceMutation(aquariumId);

  const imageIds = useMemo(
    () =>
      (cameraSources ?? [])
        .map((source) => source.lastReceivedImageId)
        .filter((id): id is string => typeof id === 'string' && id.length > 0),
    [cameraSources],
  );

  // Exact lookup of each source's last received image by id.
  const lastImages = useCameraSourceImages(aquariumId, imageIds);

  const [formState, setFormState] = useState<FormState | null>(null);
  const [submitError, setSubmitError] = useState(false);
  const [tokenResult, setTokenResult] = useState<CameraSourceWithToken | null>(null);
  const [revokeTarget, setRevokeTarget] = useState<CameraSource | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<CameraSource | null>(null);
  const [focusSectionHeading, setFocusSectionHeading] = useState(false);

  // After a delete the card (and its Delete button) is gone, so focus moves to
  // the section heading. This runs after ConfirmDialog's close cleanup, which
  // would otherwise restore focus to the removed button.
  useEffect(() => {
    if (!focusSectionHeading) return;
    setFocusSectionHeading(false);
    document.querySelector<HTMLElement>(`.${SECTION_HEADING_CLASS}`)?.focus();
  }, [focusSectionHeading]);

  const addButtonRef = useRef<HTMLButtonElement>(null);

  const isError = sourcesError;
  const isLoading = !isError && cameraSources === undefined;

  const handleRetry = () => {
    void refetch();
  };

  const openCreateForm = () => {
    setSubmitError(false);
    setFormState({ mode: 'create' });
  };

  const openEditForm = (source: CameraSource) => {
    setSubmitError(false);
    setFormState({ mode: 'edit', source });
  };

  const closeForm = () => {
    setFormState(null);
    setSubmitError(false);
  };

  const handleFormSubmit = (values: CameraSourceFormValues) => {
    if (!formState) return;
    setSubmitError(false);
    const input = { name: values.name, expectedCaptureTimes: values.expectedCaptureTimes };

    if (formState.mode === 'create') {
      createMutation.mutate(input, {
        onSuccess: (created) => {
          setFormState(null);
          setTokenResult(created);
        },
        onError: () => setSubmitError(true),
      });
      return;
    }

    updateMutation.mutate(
      { id: formState.source.id, input },
      {
        onSuccess: () => setFormState(null),
        onError: () => setSubmitError(true),
      },
    );
  };

  const handleTokenConfirm = () => {
    setTokenResult(null);
    showToast(t('cameraSources.toast.created'), 'success');
    void refetch();
    addButtonRef.current?.focus();
  };

  const handleRevokeConfirm = () => {
    if (!revokeTarget) return;
    revokeMutation.mutate(revokeTarget.id, {
      onSuccess: () => {
        setRevokeTarget(null);
        showToast(t('cameraSources.toast.revoked'), 'success');
      },
      onError: () => {
        // Dialog stays open so the owner can retry.
        showToast(t('cameraSources.error.description'), 'error');
      },
    });
  };

  // Stable identity: ConfirmDialog re-runs its focus effect when onCancel changes.
  const handleDeleteCancel = useCallback(() => setDeleteTarget(null), []);

  const handleDeleteConfirm = () => {
    if (!deleteTarget || deleteMutation.isPending) return;
    deleteMutation.mutate(deleteTarget.id, {
      onSuccess: () => {
        setDeleteTarget(null);
        setFocusSectionHeading(true);
        showToast(t('cameraSources.toast.deleted'), 'success');
      },
      onError: () => {
        // Dialog stays open so the owner can retry.
        showToast(t('cameraSources.deleteConfirm.error'), 'error');
      },
    });
  };

  const renderContent = () => {
    if (isError) {
      return (
        <LoadErrorState
          title={t('cameraSources.error.title')}
          titleLevel={3}
          message={t('cameraSources.error.description')}
          onRetry={handleRetry}
          retryLabel={t('cameraSources.retry')}
        />
      );
    }

    if (isLoading || cameraSources === undefined) {
      return (
        <Grid minItemWidth="sm" data-testid="camera-sources-loading">
          {Array.from({ length: LOADING_PLACEHOLDER_COUNT }, (_, index) => (
            <Card key={index}>
              <Stack gap="2">
                <Text variant="muted">{t('cameraSources.loading')}</Text>
              </Stack>
            </Card>
          ))}
        </Grid>
      );
    }

    if (cameraSources.length === 0) {
      return (
        <EmptyState
          icon={<Icon name="camera" />}
          headline={t('cameraSources.empty.title')}
          description={t('cameraSources.empty.description')}
          headingLevel={3}
          actionLabel={t('cameraSources.add')}
          onAction={openCreateForm}
        />
      );
    }

    return (
      <Grid as="ul" minItemWidth="md" className="camera-sources-panel__list">
        {cameraSources.map((source) => (
          <li key={source.id} className="camera-sources-panel__item" data-testid="camera-source-card">
            <CameraSourceCard
              id={source.id}
              name={source.name}
              status={source.status}
              lastReceivedImage={
                source.lastReceivedImageId
                  ? toLastReceivedImage(lastImages.get(source.lastReceivedImageId))
                  : undefined
              }
              expectedCaptureTimes={source.expectedCaptureTimes ?? []}
              onEdit={() => openEditForm(source)}
              onRevoke={() => setRevokeTarget(source)}
              onDelete={() => setDeleteTarget(source)}
            />
          </li>
        ))}
      </Grid>
    );
  };

  return (
    <>
      <Section
        title={t('cameraSources.section.title')}
        description={t('cameraSources.subtitle')}
        headingProps={{ className: SECTION_HEADING_CLASS, tabIndex: -1 }}
        actions={
          // Without sources the empty state carries the "Add" action.
          cameraSources?.length === 0 ? undefined : (
            <Button ref={addButtonRef} variant="primary" disabled={isLoading || isError} onClick={openCreateForm}>
              {t('cameraSources.add')}
            </Button>
          )
        }
        aria-live="polite"
        aria-busy={isLoading}
      >
        {renderContent()}
      </Section>
      {formState !== null && (
        <CameraSourceForm
          isOpen
          initialValues={
            formState.mode === 'edit'
              ? {
                  name: formState.source.name,
                  expectedCaptureTimes: [...(formState.source.expectedCaptureTimes ?? [])],
                }
              : undefined
          }
          onSubmit={handleFormSubmit}
          onCancel={closeForm}
          submitError={submitError}
          isSubmitting={createMutation.isPending || updateMutation.isPending}
        />
      )}

      {tokenResult !== null && (
        <TokenRevealDialog
          isOpen
          endpointUrl={ingestEndpointUrl(tokenResult.id)}
          headerName={DEVICE_TOKEN_HEADER}
          token={tokenResult.token}
          onConfirm={handleTokenConfirm}
        />
      )}

      <RevokeConfirmationDialog
        isOpen={revokeTarget !== null}
        sourceName={revokeTarget?.name ?? ''}
        onCancel={() => setRevokeTarget(null)}
        onConfirm={handleRevokeConfirm}
        isSubmitting={revokeMutation.isPending}
      />

      <ConfirmDialog
        isOpen={deleteTarget !== null}
        title={t('cameraSources.deleteConfirm.title')}
        message={t('cameraSources.deleteConfirm.description', { name: deleteTarget?.name ?? '' })}
        cancelLabel={t('cameraSources.deleteConfirm.cancel')}
        confirmLabel={t('cameraSources.deleteConfirm.confirm')}
        onCancel={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
      />
    </>
  );
}

export default CameraSourcesPanel;
